// Returning-client recognition (TW-0037), the form's half.
//
// Once the client has typed BOTH a phone and an email, ask the API once whether they have signed
// at THIS shop before, and fill in the contact details they gave last time. A first-time client
// sees nothing at all — no line, no flash, no error.
//
// The privacy rule lives on the API side (services/waiver-api/consolidated_api/recognition.py, and
// docs/DECISIONS.md 2026-09-04): both halves must match, at this shop, and only the client's own
// ordinary contact details come back. This file trusts none of that — it takes only the fields it
// knows about, and every one it writes stays a normal editable input.
//
// It never overwrites something the client already typed. Anything they filled in stays theirs.

import { API_URL } from "./config";

/** Exactly the fields the API is allowed to return. Anything else in the payload is ignored here
 *  too, so widening the API without widening this is a no-op rather than a surprise. */
export const PREFILL_FIELDS = [
  "firstName",
  "lastName",
  "birthDateMonth",
  "birthDateDay",
  "birthDateYear",
  "address1",
  "address2",
  "city",
  "state",
  "zip",
] as const;

export type PrefillField = (typeof PREFILL_FIELDS)[number];
export type RecognizedClient = Partial<Record<PrefillField, string>>;

/** TW-0195 — does THIS shop offer the "Been here before?" action at the top of its form? OFF for
 *  every shop that has not turned it on in Settings → Waiver form, which is every shop that
 *  predates the setting: absent or false and the action is not drawn at all. Only a literal `true`
 *  counts — a string, a 1, an empty object is a row somebody wrote by hand, and "not offered" is
 *  the safe direction to be wrong in (`enabledLangs` in i18n.ts is the same shape for the same
 *  reason). The decision lives HERE and not in the JSX, so one test can hold it.
 *
 *  It gates the ACTION and nothing else. The automatic recognition that has run since TW-0037 is
 *  untouched by it, because that one asks only about a phone and an email the client has already
 *  typed into the form themselves. */
export function offersReturningLookup(publicData?: { returning_lookup?: unknown } | null): boolean {
  return (publicData ?? null) !== null && publicData!.returning_lookup === true;
}

// ── ON THE DEMO IT DEMOS (TW-0195) ─────────────────────────────────────────────────────────────
//
// The setting is on for demo.waivers.tattoo so a prospect can see the action. Chris tried it there
// the morning it shipped and got "We did not find you." — which is the correct answer, because a
// prospect has never signed at the demo shop, and a correct answer that shows nothing is a broken
// demo. Chris, 2026-09-21, verbatim: "for the demo mode i wonder if we need a better demo ... this
// is "accurate" for the real form but how do we want to do this?" and "a demo banner saying "enter
// any valid phone / email" and maybe making it not required, and then filling out the form with
// complete fake data?"
//
// So on the demo, and ONLY there, the lookup answers with a made-up client instead of asking the
// API. A real shop's form is byte for byte what it was: these two exports are read from nowhere but
// a branch gated on `isDemoShop` (subdomain.ts), the same gate the state picker and the pairing
// ride.

/** The pair the panel opens pre-filled with, so a prospect taps "Find me" and types nothing.
 *
 *  ⚠ THE EMAIL MUST BE AT OUR OWN DOMAIN. A prospect who has seen the fill often goes on to SIGN
 *  the demo waiver, and the demo shop then sends its thank-you to whatever address is in the email
 *  box — this one. `demo-client@` at waivers.tattoo lands in our own catch-all (banked, classified
 *  as ours) and nothing bounces. An `example.com` or `example.org` address would HARD-bounce, and
 *  hard bounces are counted per ACCOUNT by SES — the same account the sign-in codes and every
 *  shop's mail ride on. A made-up address at a domain we do not own is a reputation cost, not a
 *  placeholder. 555-01xx is the reserved fictional block, so the phone costs nobody anything. */
export const DEMO_LOOKUP_PAIR = {
  phone: "(317) 555-0142",
  email: "demo-client@waivers.tattoo",
} as const;

/** The made-up client the demo's "Find me" fills in — exactly the `PREFILL_FIELDS` keys and
 *  nothing else, so it goes through the same `applyPrefill` a real match goes through and inherits
 *  every rule that has: nothing the visitor already typed is overwritten, and the age is derived
 *  from the birth date rather than carried.
 *
 *  A fresh object each call: `applyPrefill` does not mutate it, but a module-level singleton handed
 *  to form state is the kind of thing a later edit turns into a shared-reference bug. Pure, no
 *  network, no clock. */
export function demoRecognition(): RecognizedClient {
  return {
    firstName: "Sam",
    lastName: "Rivera",
    birthDateMonth: "06",
    birthDateDay: "12",
    birthDateYear: "1994",
    address1: "123 Demo Street",
    city: "Indianapolis",
    // the two-letter form, which is what the form itself writes into that box: the Google Places
    // pick asks for `administrative_area_level_1` short (consolidated_api/address.py). The box is a
    // free-text input, not a select — there is no option list to match.
    state: "IN",
    zip: "46203",
  };
}

/** Enough of a US phone to be worth asking about: 10 digits, or 11 starting with a 1. Deliberately
 *  looser than the API's parser — the API is the one that decides, this just stops us asking on
 *  every keystroke of an area code. */
export function phoneLooksComplete(raw: string): boolean {
  const digits = (raw || "").replace(/\D/g, "");
  return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
}

export function emailLooksComplete(raw: string): boolean {
  const text = (raw || "").trim();
  if (text.length < 6 || /\s/.test(text)) return false;
  const at = text.indexOf("@");
  if (at < 1 || at !== text.lastIndexOf("@")) return false;
  const domain = text.slice(at + 1);
  return /^[^.@]+(\.[^.@]+)+$/.test(domain);
}

/** One string per (phone, email) pair, so a client who keeps typing is asked about once. */
export function lookupKey(phone: string, email: string): string {
  return `${(phone || "").replace(/\D/g, "")}|${(email || "").trim().toLowerCase()}`;
}

/** Ask. Never throws, never surfaces an error to the client: anything that is not a clean
 *  `recognized: true` is "we do not know you", which is exactly what a first-time client sees.
 *  An older API with no /recognize route lands here as a 403/404 and reads the same way. */
export async function fetchRecognition(
  subdomain: string,
  phone: string,
  email: string,
  signal?: AbortSignal
): Promise<RecognizedClient | null> {
  try {
    // POST, not GET: a client's phone and email do not belong in a URL that an access log keeps.
    const response = await fetch(`${API_URL}/recognize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subdomain, phone, email }),
      signal,
    });
    if (response.status !== 200) return null;
    const payload = await response.json();
    if (!payload?.data?.recognized || !payload?.data?.client) return null;
    const client: RecognizedClient = {};
    for (const field of PREFILL_FIELDS) {
      const value = payload.data.client[field];
      if (typeof value === "string" && value.trim()) client[field] = value.trim();
    }
    return Object.keys(client).length ? client : null;
  } catch {
    return null;
  }
}

/** What the form state becomes. Only EMPTY fields are filled — a client who has already typed
 *  their name keeps the name they typed — and `filled` is what actually changed, so the welcome
 *  line only appears when something really was filled in. The age is not one of them (TW-0503,
 *  2026-10-29): the page reads it off the birth date filled in here, on the day of signing, so a
 *  returning client's age is never a year stale. */
export function applyPrefill<T extends Record<string, any>>(
  form: T,
  client: RecognizedClient
): { form: T; filled: string[] } {
  const next: Record<string, any> = { ...form };
  const filled: string[] = [];
  for (const field of PREFILL_FIELDS) {
    const value = client[field];
    if (!value) continue;
    if (`${next[field] ?? ""}`.trim()) continue; // theirs, already typed
    next[field] = value;
    filled.push(field);
  }
  return { form: next as T, filled };
}
